/**
 * Posters (sections/hero-collection.liquid, assets/analog.css), 1960s only:
 * a hero collection's photo hung in a frame under glass, or the many on a
 * collection page's poster wall (sections/collection-poster-wall.liquid,
 * assets/poster-wall.js). Under a mouse a
 * poster pivots a few degrees on its middle, the corner nearest the
 * pointer coming forward, as a print does in a print row
 * (assets/print-row.js); its glass throws the light back the other way as
 * it turns, which the stylesheet draws from the two angles set here.
 *
 *   --tilt-x   degrees about the horizontal axis, down to the pointer
 *   --tilt-y   degrees about the vertical axis, the pointer's left
 *
 * Each angle follows the pointer on a spring, so a poster eases toward a
 * pose and settles back flat, a little overshooting, when the pointer
 * leaves. Nothing happens for a finger, a pen, or a visitor who asked for
 * less motion. The frame loop runs only while a poster is moving: one that
 * has come to rest, flat or leaning toward a still pointer, waits for the
 * pointer to move or the page to scroll it, and a pose is written only when
 * it changes, as each write restyles the whole poster.
 *
 * The listeners are on the document, so sections the theme editor renders
 * again need no set-up, and every hero collection and wall loads this once.
 */
(() => {
  if (window.AnalogPosters) return;
  window.AnalogPosters = true;
  const SELECTOR = ".poster";
  const TILT = 3.5; // degrees at the poster's edge
  const STIFFNESS = 170;
  const DAMPING = 21;
  const REST = 0.005; // degrees, and degrees a second, below which it is flat
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  // Posters off the flat, each with its angles, speeds and where it is headed.
  const bodies = new Map();
  let pointer = null;
  let frame = 0;
  let last = 0;
  const clamp = (value) => Math.max(-1, Math.min(1, value));
  // Where a poster should lean for the pointer: nothing without one or
  // outside the poster, else toward it by how far it is from the middle.
  // The poster's own box is read, not the pivoting art's, so the pose
  // never moves the point it is measured from.
  const goalFor = (poster) => {
    if (!pointer || reducedMotion.matches) return [0, 0];
    const box = poster.getBoundingClientRect();
    const dx = (pointer.x - box.left - box.width / 2) / (box.width / 2);
    const dy = (pointer.y - box.top - box.height / 2) / (box.height / 2);
    if (Math.abs(dx) > 1 || Math.abs(dy) > 1) return [0, 0];
    return [clamp(dy) * TILT, -clamp(dx) * TILT];
  };
  const write = (poster, body) => {
    const [x, y] = body.value.map((value) => value.toFixed(3));
    const pose = `${x} ${y}`;
    if (body.pose === pose) return;
    body.pose = pose;
    poster.style.setProperty("--tilt-x", x);
    poster.style.setProperty("--tilt-y", y);
    if (!poster.hasAttribute("data-tilting"))
      poster.setAttribute("data-tilting", "");
  };
  const release = (poster) => {
    poster.style.removeProperty("--tilt-x");
    poster.style.removeProperty("--tilt-y");
    poster.removeAttribute("data-tilting");
    bodies.delete(poster);
  };
  const tick = (now) => {
    /* A first frame's time can be earlier than performance.now() at start. */
    const dt = Math.max(0, Math.min((now - last) / 1000, 1 / 30));
    last = now;
    let anyMoving = false;
    bodies.forEach((body, poster) => {
      if (!poster.isConnected) return bodies.delete(poster);
      const goal = goalFor(poster);
      const moving = body.value
        .map((value, axis) => {
          body.velocity[axis] +=
            (-STIFFNESS * (value - goal[axis]) -
              DAMPING * body.velocity[axis]) *
            dt;
          body.value[axis] += body.velocity[axis] * dt;
          return (
            Math.abs(body.value[axis] - goal[axis]) > REST ||
            Math.abs(body.velocity[axis]) > REST
          );
        })
        .some(Boolean);
      if (!moving && !goal.some(Boolean)) return release(poster);
      write(poster, body);
      if (moving) anyMoving = true;
    });
    frame = anyMoving ? requestAnimationFrame(tick) : 0;
  };
  const start = () => {
    if (frame) return;
    last = performance.now();
    frame = requestAnimationFrame(tick);
  };
  const follow = (poster) => {
    if (!poster) return;
    if (!bodies.has(poster))
      bodies.set(poster, { value: [0, 0], velocity: [0, 0] });
    start();
  };
  document.addEventListener(
    "pointermove",
    (event) => {
      if (event.pointerType !== "mouse" || reducedMotion.matches) return;
      pointer = { x: event.clientX, y: event.clientY };
      follow(event.target.closest?.(SELECTOR));
      // Off every poster: one left leaning, the loop stopped, eases back.
      if (bodies.size) start();
    },
    { passive: true }
  );
  // A wall of posters scrolled under a still mouse brings another under it:
  // looked for once a frame, however many scroll events it brings, as
  // finding what is under the mouse lays the page out.
  let scrolled = 0;
  document.addEventListener(
    "scroll",
    (event) => {
      if (!pointer || reducedMotion.matches || scrolled) return;
      // Only a scroll that moves a poster: the page, or a scroller with one
      // in it, not every other row on the page.
      if (!event.target.querySelector?.(SELECTOR)) return;
      scrolled = requestAnimationFrame(() => {
        scrolled = 0;
        if (!pointer) return;
        follow(
          document.elementFromPoint(pointer.x, pointer.y)?.closest(SELECTOR)
        );
        // Those already leaning follow the scroll too: the loop stopped
        // once they settled.
        if (bodies.size) start();
      });
    },
    { passive: true, capture: true }
  );
  // Out of the window altogether: every poster eases back flat.
  document.documentElement.addEventListener(
    "pointerleave",
    () => {
      pointer = null;
      if (bodies.size) start();
    },
    { passive: true }
  );
})();
